import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { extractErr } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type EventDef = {
  id: number
  code: string
  label: string
  category: string
}

type Pref = {
  id: number
  event_code: string
  channel: 'IN_APP' | 'EMAIL'
  is_enabled: boolean
}

// We need EventDefinitions to render the grid. The admin endpoint
// `/notifications/events/` is permission-gated. For users without that
// permission we fall back to deriving the event list from the user's
// own notifications history — coarser but still useful.
type NotificationLite = { event_code: string }

const CHANNELS: Array<'IN_APP' | 'EMAIL'> = ['IN_APP', 'EMAIL']
const CHANNEL_LABEL: Record<'IN_APP' | 'EMAIL', string> = {
  IN_APP: 'In-app',
  EMAIL: 'Email',
}

export default function MyNotificationPreferences() {
  const qc = useQueryClient()

  const eventsQ = useQuery({
    queryKey: ['notif-events'],
    queryFn: async () => (await api.get<EventDef[]>('/notifications/events/')).data,
    retry: false,
  })

  // Fallback list — what events I've actually received in the past.
  // Lets non-admin users still see + tweak the relevant rows.
  const historyQ = useQuery({
    queryKey: ['notifications', 'history-codes'],
    queryFn: async () => (await api.get<NotificationLite[]>('/notifications/?limit=200')).data,
    enabled: eventsQ.isError,   // only used if events fetch failed (403)
  })

  const prefsQ = useQuery({
    queryKey: ['notif-prefs'],
    queryFn: async () => (await api.get<Pref[]>('/notifications/preferences/')).data,
  })

  // Local state: map of "code|channel" → enabled (boolean). Missing key = enabled (default).
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map())
  useEffect(() => {
    if (prefsQ.data) {
      const m = new Map<string, boolean>()
      for (const p of prefsQ.data) m.set(`${p.event_code}|${p.channel}`, p.is_enabled)
      setOverrides(m)
    }
  }, [prefsQ.data])

  function key(code: string, channel: string) { return `${code}|${channel}` }
  function isEnabled(code: string, channel: string): boolean {
    const v = overrides.get(key(code, channel))
    return v === undefined ? true : v
  }
  function setEnabled(code: string, channel: string, value: boolean) {
    const m = new Map(overrides)
    m.set(key(code, channel), value)
    setOverrides(m)
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      // Send the full override map — backend upserts each row.
      const preferences = Array.from(overrides.entries()).map(([k, v]) => {
        const [event_code, channel] = k.split('|')
        return { event_code, channel, is_enabled: v }
      })
      return (await api.put<{ updated: number; preferences: Pref[] }>(
        '/notifications/preferences/', { preferences },
      )).data
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['notif-prefs'] })
      toast.success(`Saved · ${res.updated} preference${res.updated === 1 ? '' : 's'} updated`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Decide which events to render. Prefer the registry; fall back to history.
  const events: EventDef[] = (() => {
    if (eventsQ.data) return eventsQ.data
    if (!historyQ.data) return []
    // Synthesise minimal EventDef rows from observed codes.
    const seen = Array.from(new Set(historyQ.data.map((n) => n.event_code))).sort()
    return seen.map((code, i) => ({
      id: i,
      code,
      label: code,
      category: (code.split('.')[0] || 'Other').toUpperCase(),
    }))
  })()

  const grouped = new Map<string, EventDef[]>()
  for (const e of events) {
    const c = e.category || 'Other'
    if (!grouped.has(c)) grouped.set(c, [])
    grouped.get(c)!.push(e)
  }
  const categories = Array.from(grouped.keys()).sort()

  return (
    <AdminLayout>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Notification preferences</h2>
          <p className="text-sm text-muted-foreground">
            Turn off specific event × channel combinations. Default is everything on.
          </p>
        </div>
        <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
          {saveMut.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>

      {(eventsQ.isLoading || prefsQ.isLoading) ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : events.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No notification events to configure yet.
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map((cat) => (
            <div key={cat} className="bg-card rounded-lg shadow overflow-hidden">
              <div className="px-4 py-2 bg-muted/30 border-b border-border">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {cat}
                </h3>
              </div>
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Event
                    </th>
                    {CHANNELS.map((c) => (
                      <th key={c} className="px-4 py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide w-28">
                        {CHANNEL_LABEL[c]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {grouped.get(cat)!.map((e) => (
                    <tr key={e.code} className="hover:bg-muted/40">
                      <td className="px-4 py-2 text-sm text-foreground">
                        <div className="font-medium">{e.label}</div>
                        <div className="text-xs text-muted-foreground font-mono">{e.code}</div>
                      </td>
                      {CHANNELS.map((c) => (
                        <td key={c} className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isEnabled(e.code, c)}
                            onChange={(ev) => setEnabled(e.code, c, ev.target.checked)}
                            aria-label={`${e.label} via ${CHANNEL_LABEL[c]}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  )
}
